import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, LogOut, Mail, Stethoscope, UserRound } from "lucide-react";
import MobileShell from "@/components/MobileShell";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <Icon size={18} className="mt-0.5 shrink-0 text-teal-600" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.13em] text-slate-500">{label}</p>
        <p className="mt-1 break-words text-sm font-medium text-slate-800">{value}</p>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/dashboard/me")
      .then((response) => {
        if (active) setDashboard(response.data || {});
      })
      .catch(() => {
        if (active) setDashboard({});
      });
    return () => { active = false; };
  }, []);

  const isExerciseGuest = user?.account_type === "exercise_guest";
  const therapist = dashboard?.assigned_therapist;
  const activePlan = dashboard?.active_home_plan || dashboard?.active_program_plan;
  const hasPathway = Boolean(dashboard?.condition);
  const programName = dashboard?.condition?.name || dashboard?.condition?.program?.["Program Name"];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <MobileShell>
      <main className="px-5 pb-28 pt-8 sm:px-6">
        <header className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Me</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Me</h1>
          <p className="mt-2 text-lg font-semibold text-slate-900">{user?.name || user?.display_name}</p>
          <p className="mt-0.5 break-words text-sm text-slate-500">{user?.email}</p>
          {isExerciseGuest && <span className="mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">Exercise Program Only</span>}
        </header>

        {therapist && (
          <section className="mb-4 rounded-2xl border border-teal-100 bg-teal-50/60 p-4" data-testid="my-physiotherapist">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-teal-700">My Physiotherapist</p>
            <div className="mt-3 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-teal-700"><Stethoscope size={19} aria-hidden="true" /></span>
              <div><p className="font-semibold text-slate-900">{therapist.name}</p>{therapist.clinic_name && <p className="text-sm text-slate-600">{therapist.clinic_name}</p>}</div>
            </div>
          </section>
        )}

        <section className="mb-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid="my-program">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">{hasPathway ? "My Recovery Program" : activePlan ? "My Exercise Program" : "My Program"}</p>
          {hasPathway ? (
            <p className="mt-2 font-semibold text-slate-900">{programName || "Your Recovery Program"}</p>
          ) : activePlan ? (
            <p className="mt-2 font-semibold text-slate-900">{activePlan.title || "Your Exercise Program"}</p>
          ) : (
            <p className="mt-2 text-sm leading-6 text-slate-500">Your physiotherapist has not assigned a program yet.</p>
          )}
        </section>

        <section className="mb-4 rounded-2xl border border-slate-100 bg-white px-4 shadow-sm" aria-labelledby="account-heading">
          <h2 id="account-heading" className="pt-4 text-sm font-semibold text-slate-900">Account</h2>
          <DetailRow icon={UserRound} label="Name" value={user?.name || user?.display_name || ""} />
          <div className="border-t border-slate-100"><DetailRow icon={Mail} label="Email" value={user?.email || ""} /></div>
        </section>

        {dashboard?.clinic_contact && (
          <section className="mb-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" aria-labelledby="help-heading">
            <h2 id="help-heading" className="font-semibold text-slate-900">Need help?</h2>
            <a href={dashboard.clinic_contact.href} className="mt-3 flex items-center gap-2 text-sm font-semibold text-teal-700">
              <Building2 size={17} aria-hidden="true" /> Contact Clinic
            </a>
          </section>
        )}

        <Button data-testid="logout-btn" variant="outline" onClick={handleLogout} className="mt-4 h-12 w-full rounded-full border-slate-300 text-slate-700">
          <LogOut size={16} className="mr-2" /> Log out
        </Button>
      </main>
      <BottomNav />
    </MobileShell>
  );
}
